<template>
  <div ref="el" :style="style" class="draggable" @mousedown="onMouseDown" @touchstart="onTouchStart">
    <slot :dragging="dragging" />
  </div>
</template>

<script>
import { computed, defineComponent, onBeforeUnmount, onMounted, reactive, ref, watch } from "@vue/composition-api";
import appStorage from "@/lib/appStorage";

const STORAGE_KEY = "draggable-position";

export default defineComponent({
  name: "DraggableWrapper",
  setup() {
    const el = ref(null);
    const dragging = ref(false);

    const offset = 16;
    const snapOffset = offset + 50;

    let startX, startY;

    const pos = reactive({
      x: offset,
      y: offset,
      top: offset,
      left: offset,
      right: 0,
      bottom: 0,
    });

    const viewport = reactive({
      width: document.documentElement.clientWidth,
      height: document.documentElement.clientHeight,
      safePosition: document.documentElement.clientWidth > 1000 ? 0 : 56,
    });

    const style = computed(() => ({
      top: pos.top ? pos.top + "px" : undefined,
      left: pos.left ? pos.left + "px" : undefined,
      right: pos.right ? pos.right + "px" : undefined,
      bottom: pos.bottom ? `calc(env(safe-area-inset-bottom) + ${pos.bottom}px)` : undefined,
    }));

    const moveTo = (x, y) => {
      const maxY = viewport.height - el.value.offsetHeight - offset - viewport.safePosition;
      const maxX = viewport.width - el.value.offsetWidth - offset;

      y = Math.min(Math.max(offset, y), maxY);
      x = Math.min(Math.max(offset, x), maxX);

      // snap
      pos.y =
        y < snapOffset
          ? offset
          : y > viewport.height - el.value.offsetHeight - snapOffset - viewport.safePosition
          ? maxY
          : y;
      pos.x = x < snapOffset ? offset : x > viewport.width - el.value.offsetWidth - snapOffset ? maxX : x;

      if (pos.y > viewport.height / 2) {
        pos.top = 0;
        pos.bottom = viewport.height - pos.y - el.value.offsetHeight;
      } else {
        pos.top = pos.y;
        pos.bottom = 0;
      }
      if (pos.x > viewport.width / 2) {
        pos.left = 0;
        pos.right = viewport.width - pos.x - el.value.offsetWidth;
      } else {
        pos.left = pos.x;
        pos.right = 0;
      }
    };

    const loadPosition = () => {
      const saved = appStorage.getString(STORAGE_KEY, "");
      if (saved) {
        try {
          const { x, y } = JSON.parse(saved);
          moveTo(x, y);
        } catch (_) {}
      }
    };

    const savePosition = () => {
      appStorage.setString(STORAGE_KEY, JSON.stringify({ x: pos.x, y: pos.y }));
    };

    const onMouseMove = e => {
      if (!dragging.value) return;
      moveTo(e.clientX - startX, e.clientY - startY);
    };

    const onMouseUp = () => {
      dragging.value = false;
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };

    const onMouseDown = e => {
      dragging.value = true;
      startX = e.clientX - pos.x;
      startY = e.clientY - pos.y;
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    };

    const onTouchMove = e => {
      if (!dragging.value) return;
      const touch = e.touches[0];
      moveTo(touch.clientX - startX, touch.clientY - startY);
      e.preventDefault();
    };

    const onTouchEnd = () => {
      dragging.value = false;
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };

    const onTouchStart = e => {
      const touch = e.touches[0];
      dragging.value = true;
      startX = touch.clientX - pos.x;
      startY = touch.clientY - pos.y;
      window.addEventListener("touchmove", onTouchMove, { passive: false });
      window.addEventListener("touchend", onTouchEnd);
    };

    const onResize = () => {
      viewport.width = document.documentElement.clientWidth;
      viewport.height = document.documentElement.clientHeight;
      viewport.safePosition = document.documentElement.clientWidth > 1000 ? 0 : 56;
      loadPosition();
    };

    watch(dragging, dragging => {
      !dragging && savePosition();
    });

    onMounted(() => {
      loadPosition();
      window.addEventListener("resize", onResize);
    });

    onBeforeUnmount(() => {
      window.removeEventListener("resize", onResize);
    });

    return {
      el,
      style,
      dragging,
      onMouseDown,
      onTouchStart,
    };
  },
});
</script>

<style lang="scss" scoped>
.draggable {
  position: fixed;
  cursor: grab;
  user-select: none;
  touch-action: none;
  z-index: 13;
}
</style>
